import React, { FC, useEffect, useState } from "react";
import { DefaultButton, mergeStyles, mergeStyleSets, PrimaryButton } from "@fluentui/react";
import { MessageBar } from "@fluentui/react";
import { MessageBarType } from "@fluentui/react";
import { PatchModel, TruckNomination } from "../../../models";
import { Patch } from "../../../api/apiUtils";
import SofIcon from "../../common/Icons";
import { FontIcon } from "@fluentui/react";
import clsx from "clsx";
import { stringify, trimEmpty } from "../../../services/Helper";

type Props = TruckNomination & {
  lineHandlingStatus: number;
  onSuccess(result: TruckNomination): void;
  onClose(): void;
};

const button = mergeStyles({
  padding: "9px 10px",
  fontSize: 16,
  fontWeight: 500,
  height: 42,
  border: "none",
  flex: "1 1 auto",
  marginLeft: 5,
  selectors: {
    [":active"]: {
      backgroundColor: "#008cc0"
    }
  }
});
const table = mergeStyles({
  background: "#FAFAFA",
  overflow: "hidden"
});

const classes = mergeStyleSets({
  container: {
    flex: "1 1 auto",
    display: "flex",
        flexDirection: "column",
    height: "inherit",
    background: "#FFFFFF",
    zIndex: 1,
    border: "1px solid #E2E6E8"
  },
  headerContainer: {
    padding: "15px 10px",
      backgroundColor: "#FEC40D",
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold"
  },
  bodyContainer: {
    padding: "15px 10px",
    flex: "1 1 auto",
    height: 0,
    overflowY: "auto"
  },
  detailSwitch: {
    padding: "0 0 10px 0",
    color: "#4C4C4C",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    selectors: {
      [":active"]: {
        backgroundColor: "#008cc0"
      }
    }
  },
  detailSwitchLabel: {
    flex: "1 1 auto"
  },
  footerContainer: {
    display: "flex",
    padding: 10
  },
  fieldLabel: {
    color: "#7C7777",
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 20,
    display: "block",
    marginTop: 12
  },
  fieldInputBox: {
    border: "1px solid #D8DFE6",
    width: "100%",
    padding: "5px 10px",
    selectors: {
      [":focus"]: {
        border: "1px solid #008cc0"
      }
    }
  },
  customInput: {
    border: "1px solid #D8DFE6",
    width: "100%",
    display: "flex"
  },
  customTextInput: {
    border: "none",
    flex: "1 1 auto",
    padding: "5px 10px"
  },
  customInputRight: {
    borderLeft: "1px solid #E2E6E8",
    padding: "6px 10px 0 10px",
    lineHeight: 1,
    backgroundColor: "#EEEEEE",
    fontSize: 14,
    color: "#4C4C4C"
  },
  submitButton: [
    button,
    {
      backgroundColor: "#009FDF",
      color: "#FFFFFF",
      margin: "0 5px 0 0",
      selectors: {
        [":disabled"]: {
          color: "#7C7777",
          backgroundColor: "#B8B8B8"
        }
      }
    }
  ]
});

const NominationForm: FC<Props> = (props: Props) => {
  const [containerType, setContainerType] = useState<string>();
  const [carrier, setCarrier] = useState<string>();
  const [sealsInstalled, setSealsInstalled] = useState<string>();
  const [sealsRemoved, setSealsRemoved] = useState<string>();
  const [sampleTags, setSampleTags] = useState<string>();
  const [showDetails, setShowDetails] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const onSave = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault();
      setIsLoading(true);
      setErrorMessage(undefined);

      const payload: PatchModel[] = [
        {
          op: "replace",
          path: "/container",
          value: containerType
        },
        {
          op: "replace",
          path: "/carrier",
          value: carrier
        },
        {
          op: "replace",
          path: "/sealsInstalled",
          value: sealsInstalled
        },
        {
          op: "replace",
          path: "/sealsRemoved",
          value: sealsRemoved
        },
        {
          op: "replace",
          path: "/sampleTags",
          value: sampleTags
        }
      ];
      const response = await Patch(
        `/api/nomination/${props.nominationNo}`,
        JSON.stringify(payload)
      );
      setIsLoading(false);

      if (response.ok) {
        const result = (await response.json()) as TruckNomination;
        props.onSuccess(result);
      } else {
        setErrorMessage("Unable to save changes");
      }
    } catch {
      setErrorMessage("Unable to save changes. Please try to reconnect.");

      setIsLoading(false);
    }
  };

  const getStyle = () => {
    if (showDetails) return mergeStyles([table]);
    else return mergeStyles([table], { height: 1 });
  };

  useEffect(() => {
    setContainerType(props.container);
    setCarrier(props.carrier);
    setSealsInstalled(props.sealsInstalled);
    setSealsRemoved(props.sealsRemoved);
    setSampleTags(props.sampleTags);
  }, [
    props.carrier,
    props.container,
    props.sealsInstalled,
    props.sealsRemoved,
    props.sampleTags
  ]);

  useEffect(() => {
    setHasChanges(
      stringify(containerType) !== stringify(props.container) ||
        stringify(sealsInstalled) !== stringify(props.sealsInstalled) ||
        stringify(sealsRemoved) !== stringify(props.sealsRemoved) ||
        stringify(carrier) !== stringify(props.carrier) ||
        stringify(sampleTags) !== stringify(props.sampleTags)
    );
  }, [containerType, sealsInstalled, sealsRemoved, carrier, sampleTags]);

  return (
    <form className={classes.container} onSubmit={onSave} style={{height:"calc(100vh - 250px)"}}>
      <div id="lbl-edit-nomination" className={classes.headerContainer}>
        Edit - Nomination#&nbsp;
        {props.nominationNo.substr(props.nominationNo.length - 6)}
      </div>
      <div className={clsx(classes.bodyContainer, "custom-scroll")}>
        <div
          id="btn-toggle-nomination-detail"
          className={classes.detailSwitch}
          onClick={() => setShowDetails(!showDetails)}
        >
          <span className={classes.detailSwitchLabel}>Nomination Details</span>
          <SofIcon iconname={showDetails ? "ArrowUpLg" : "ArrowDownLg"} />
        </div>
        <div className={getStyle()}>
          <table className={clsx("tbl-detail")}>
            <tbody>
              <tr>
                <td>
                  <dt>Container Type</dt>
                  <dd id="lbl-nomination-surveyor">{props.container}&nbsp;</dd>
                </td>
                <td>
                  <dt>Carrier</dt>
                  <dd id="lbl-nomination-dock">{props.carrier}&nbsp;</dd>
                </td>
              </tr>
              <tr>
                <td>
                  <dt>Seal Numbers Added</dt>
                  <dd id="lbl-nomination-direction">
                    {props.sealsInstalled}&nbsp;
                  </dd>
                </td>
                <td>
                  <dt>Seal Numbers Removed</dt>
                  <dd id="lbl-nomination-dock">{props.sealsRemoved}&nbsp;</dd>
                </td>
              </tr>
              <tr>
                <td colSpan={2}>
                  <dt>Sample Tags</dt>
                  <dd id="lbl-nomination-customer">{props.sampleTags}&nbsp;</dd>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div>
          <label htmlFor="txt-vapor-line" className={classes.fieldLabel}>
            Carrier
          </label>
          <input
            id="txt-vapor-line"
            className={classes.fieldInputBox}
            value={carrier}
            onChange={(e) => setCarrier(trimEmpty(e.target.value))}
            onBlur={(e) => setCarrier(e.target.value.trim())}
            disabled={isLoading}
            autoFocus={props.transferType !== "OTS Transfer"}
            maxLength={150}
          />
        </div>
        <div>
          <label htmlFor="txt-ship-line" className={classes.fieldLabel}>
            Container Type
          </label>
          <input
            id="txt-ship-line"
            className={classes.fieldInputBox}
            value={containerType}
            onChange={(e) => setContainerType(trimEmpty(e.target.value))}
            onBlur={(e) => setContainerType(e.target.value.trim())}
            disabled={isLoading}
            autoFocus={props.transferType !== "OTS Transfer" ? false : true}
            maxLength={150}
          />
        </div>

        <div>
          <label htmlFor="txt-ship-line" className={classes.fieldLabel}>
            Seals Number Added
          </label>
          <input
            id="txt-ship-line"
            className={classes.fieldInputBox}
            value={sealsInstalled}
            onChange={(e) => setSealsInstalled(trimEmpty(e.target.value))}
            onBlur={(e) => setSealsInstalled(e.target.value.trim())}
            disabled={isLoading}
            autoFocus={props.transferType !== "OTS Transfer" ? false : true}
            maxLength={150}
          />
        </div>

        <div>
          <label htmlFor="txt-ship-line" className={classes.fieldLabel}>
            Seals Number Removed
          </label>
          <input
            id="txt-ship-line"
            className={classes.fieldInputBox}
            value={sealsRemoved}
            onChange={(e) => setSealsRemoved(trimEmpty(e.target.value))}
            onBlur={(e) => setSealsRemoved(e.target.value.trim())}
            disabled={isLoading}
            autoFocus={props.transferType !== "OTS Transfer" ? false : true}
            maxLength={150}
          />
        </div>

        <div>
          <label htmlFor="txt-stowage" className={classes.fieldLabel}>
            Stowage
          </label>
          <input
            id="txt-stowage"
            className={classes.fieldInputBox}
            value={sampleTags}
            onChange={(e) => setSampleTags(trimEmpty(e.target.value))}
            onBlur={(e) => setSampleTags(e.target.value.trim())}
            disabled={isLoading}
            maxLength={150}
          />
        </div>

        {errorMessage && (
          <div style={{ marginTop: "10px" }}>
            <MessageBar
              messageBarType={MessageBarType.error}
              onDismiss={() => setErrorMessage(undefined)}
            >
              {errorMessage}
            </MessageBar>
          </div>
        )}
      </div>
          <div className={classes.footerContainer}>

              <PrimaryButton
                  id="btn-save-nomination"
                  // onClick={props.onClose} saving affected by this
                  type="submit" 
                  style={{ float: "right" }}
                  disabled={!hasChanges || isLoading}
                  text={isLoading ? "Saving.." : "Save"}
              />
        {/*<button*/}
        {/*  id="btn-save-nomination"*/}
        {/*  className={classes.submitButton}*/}
        {/*  type="submit"*/}
        {/*  disabled={!hasChanges || isLoading}*/}
        {/*>*/}
        {/*  {isLoading ? "Saving.." : "Save"}*/}
        {/*      </button>*/}
              <DefaultButton
                  id="btn-cancel-nomination"
                  onClick={props.onClose}
                  style={{ float: "right" }}
                  text="Cancel"
              />
        {/*<button*/}
        {/*  id="btn-cancel-nomination"*/}
        {/*  className={button}*/}
        {/*  onClick={props.onClose}*/}
        {/*  disabled={isLoading}*/}
        {/*>*/}
        {/*  Cancel*/}
        {/*</button>*/}
      </div>
    </form>
  );
};

export default NominationForm;
